/* v3.6v — Fleet Health Dashboard
   Admin/dispatcher view of every truck's latest pre-start check, live
   maintenance flags, engine-hour meter, and 500-hour service reminders. */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Truck, ShieldCheck, AlertTriangle, Wrench, Clock, CheckCircle2, RefreshCcw,
  History, Gauge, Check, Pencil,
} from "lucide-react";

const CHECK_LABELS = {
  oil: "Oil", coolant: "Coolant", tires: "Tyres", lights: "Lights",
  brakes: "Brakes", mixer: "Mixer", hydraulics: "Hydraulics", cleanliness: "Cleanliness",
};

function statusPill(item) {
  const latest = item.latest_pre_start;
  const t = item.truck;
  if (latest && latest.overall === "fail" && !latest.resolved) {
    return { text: "Fault", cls: "border-red-600 bg-red-950/50 text-red-300" };
  }
  if ((t.status || "") === "maintenance" || (t.status || "") === "out_of_service") {
    return { text: "Maintenance", cls: "border-amber-500 bg-amber-500/10 text-amber-300" };
  }
  if (item.service_due?.due) {
    return { text: "Service Due", cls: "border-amber-500 bg-amber-500/10 text-amber-300" };
  }
  if (!item.submitted_today) {
    return { text: "No Pre-Start Today", cls: "border-slate-500 bg-slate-500/10 text-slate-300" };
  }
  return { text: "Healthy", cls: "border-emerald-500 bg-emerald-500/10 text-emerald-300" };
}

export default function FleetHealth() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyTruck, setHistoryTruck] = useState(null);
  const [history, setHistory] = useState([]);
  const [serviceTruck, setServiceTruck] = useState(null);
  const [historyTab, setHistoryTab] = useState("active");   // v3.7 · "active" | "resolved"
  const [resolveLog, setResolveLog] = useState(null);       // pre-start log being resolved
  const [editLog, setEditLog] = useState(null);             // pre-start log being edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/fleet-health");
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load fleet health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); }, [load]);

  const openHistory = async (item) => {
    setHistoryTruck(item);
    setHistoryTab("active");
    try {
      const { data } = await api.get("/admin/maintenance-logs", { params: { truck_id: item.truck.id, limit: 200 } });
      setHistory(data || []);
    } catch { setHistory([]); }
  };
  const reloadHistory = async () => {
    if (!historyTruck) return;
    try {
      const { data } = await api.get("/admin/maintenance-logs", { params: { truck_id: historyTruck.truck.id, limit: 200 } });
      setHistory(data || []);
    } catch { /* no-op */ }
    load();
  };

  const counts = rows.reduce((a, r) => {
    const activeFault = r.latest_pre_start?.overall === "fail" && !r.latest_pre_start?.resolved;
    if (activeFault || ["maintenance", "out_of_service"].includes(r.truck.status)) a.faults++;
    else if (r.service_due?.due) a.due++;
    else if (!r.submitted_today) a.pending++;
    else a.healthy++;
    return a;
  }, { faults: 0, due: 0, pending: 0, healthy: 0 });

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="fleet-health-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tag text-primary-orange">Fleet Health</div>
          <div className="heading text-2xl md:text-3xl font-black uppercase">Truck Status & Maintenance</div>
          <div className="mono text-[10px] uppercase text-muted-industrial mt-1">
            Live pre-start submissions · 500-hr service tracker · fault log
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          className="rounded-none border-industrial bg-transparent text-ink"
          data-testid="fleet-health-refresh"
        >
          <RefreshCcw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Faults" value={counts.faults} icon={AlertTriangle} tone="red" testid="kpi-faults" />
        <KpiCard label="Service Due" value={counts.due} icon={Wrench} tone="amber" testid="kpi-service-due" />
        <KpiCard label="No Check Today" value={counts.pending} icon={Clock} tone="slate" testid="kpi-pending" />
        <KpiCard label="Healthy" value={counts.healthy} icon={ShieldCheck} tone="emerald" testid="kpi-healthy" />
      </div>

      {/* Truck rows */}
      <div className="space-y-2">
        {rows.length === 0 && !loading && (
          <div className="border border-dashed border-industrial p-8 text-center text-muted-industrial">
            No trucks configured yet.
          </div>
        )}
        {rows.map((r) => {
          const pill = statusPill(r);
          const t = r.truck;
          const latest = r.latest_pre_start;
          const sv = r.service_due;
          const hoursSince = Math.max(0, (t.current_hours || 0) - (t.last_service_hours || 0));
          const pct = Math.min(100, Math.round((hoursSince / Math.max(1, t.service_hours_interval)) * 100));
          const barColor = pct >= 100 ? "bg-red-500" : pct >= 85 ? "bg-amber-500" : "bg-emerald-500";
          return (
            <div
              key={t.id}
              data-testid={`fleet-row-${t.number}`}
              className="card-industrial p-3 md:p-4 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-center"
            >
              {/* Truck badge */}
              <div className="flex items-center gap-3 md:w-52">
                <div className="w-10 h-10 border border-primary-orange text-primary-orange flex items-center justify-center">
                  <Truck className="w-5 h-5" />
                </div>
                <div>
                  <div className="mono text-[10px] uppercase text-muted-industrial">Truck</div>
                  <div className="heading text-lg font-black uppercase">{t.number}</div>
                  <div className="mono text-[10px] text-muted-industrial">{t.plate || "—"}</div>
                </div>
              </div>

              {/* Middle block */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${pill.cls}`} data-testid={`fleet-pill-${t.number}`}>
                    {pill.text}
                  </span>
                  {latest?.overall === "fail" && !latest?.resolved && latest?.failed_items?.length > 0 && (
                    <span className="mono text-[10px] text-red-300">
                      {latest.failed_items.map((k) => CHECK_LABELS[k] || k).join(" · ")}
                    </span>
                  )}
                  {t.maintenance_note && (
                    <span className="mono text-[10px] text-amber-300 truncate max-w-md">
                      Note: {t.maintenance_note}
                    </span>
                  )}
                </div>
                {/* Hours-to-service bar */}
                <div>
                  <div className="flex justify-between mono text-[10px] uppercase text-muted-industrial mb-0.5">
                    <span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> {Math.round(hoursSince)} / {Math.round(t.service_hours_interval)} hrs since service</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-surface-2">
                    <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                  </div>
                  {sv?.due && <div className="mono text-[10px] text-amber-300 mt-1">{sv.reason}</div>}
                </div>
                {latest ? (
                  <div className="mono text-[10px] text-muted-industrial">
                    Last check: {latest.date} · {latest.driver_name || "—"} · {new Date(latest.submitted_at).toLocaleTimeString()}
                  </div>
                ) : (
                  <div className="mono text-[10px] text-muted-industrial">No pre-start on record</div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col md:flex-row gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openHistory(r)}
                  className="rounded-none border-industrial bg-transparent text-ink"
                  data-testid={`fleet-history-${t.number}`}
                >
                  <History className="w-3.5 h-3.5 mr-1" /> History
                </Button>
                <Button
                  size="sm"
                  onClick={() => setServiceTruck(r)}
                  className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-[10px] text-black"
                  data-testid={`fleet-service-${t.number}`}
                >
                  <Wrench className="w-3.5 h-3.5 mr-1" /> Service…
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* History dialog · v3.7 with Active / Resolved tabs + row actions */}
      <Dialog open={!!historyTruck} onOpenChange={(o) => !o && setHistoryTruck(null)}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading uppercase tracking-wider">
              Maintenance · Truck {historyTruck?.truck?.number}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const activeAlerts = history.filter((h) => h.kind === "pre_start" && h.overall === "fail" && !h.resolved);
            const resolvedOrOther = history.filter((h) => !(h.kind === "pre_start" && h.overall === "fail" && !h.resolved));
            const rowsToShow = historyTab === "active" ? activeAlerts : resolvedOrOther;
            return (
              <>
                <div className="flex gap-2 border-b border-industrial pb-2">
                  <button
                    type="button"
                    onClick={() => setHistoryTab("active")}
                    data-testid="hist-tab-active"
                    className={`px-3 py-1 mono uppercase text-[10px] tracking-widest border transition-colors ${
                      historyTab === "active" ? "border-red-500 bg-red-500/10 text-red-300 font-bold" : "border-industrial text-muted-industrial hover:text-ink"
                    }`}
                  >
                    Active Alerts ({activeAlerts.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setHistoryTab("history")}
                    data-testid="hist-tab-history"
                    className={`px-3 py-1 mono uppercase text-[10px] tracking-widest border transition-colors ${
                      historyTab === "history" ? "border-primary-orange bg-primary-orange/10 text-primary-orange font-bold" : "border-industrial text-muted-industrial hover:text-ink"
                    }`}
                  >
                    Maintenance History ({resolvedOrOther.length})
                  </button>
                </div>
                {rowsToShow.length === 0 ? (
                  <div className="text-sm text-muted-industrial p-4 text-center border border-dashed border-industrial mt-2">
                    {historyTab === "active" ? "No active alerts — truck is healthy." : "No history yet."}
                  </div>
                ) : (
                  <div className="space-y-2 mt-2">
                    {rowsToShow.map((h) => (
                      <div
                        key={h.id}
                        className={`p-2 border ${
                          h.kind === "pre_start" && h.overall === "fail" && !h.resolved ? "border-red-600 bg-red-950/30" :
                          h.kind === "resolution" ? "border-emerald-600 bg-emerald-950/20" :
                          h.kind === "service" ? "border-primary-orange bg-primary-orange/5" :
                                                  "border-industrial"
                        }`}
                        data-testid={`history-log-${h.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="mono text-[10px] uppercase tracking-widest text-primary-orange">
                            {h.kind === "service" ? "Service" : h.kind === "resolution" ? "Fault Resolved" : "Pre-Start"} · {h.date}
                          </div>
                          <div className="flex items-center gap-1">
                            {h.overall && (
                              <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${h.overall === "pass" ? "border-emerald-500 text-emerald-400" : "border-red-500 text-red-400"}`}>
                                {h.overall}
                              </span>
                            )}
                            {h.resolved && (
                              <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-emerald-500 text-emerald-400" data-testid={`resolved-badge-${h.id}`}>
                                Resolved
                              </span>
                            )}
                            {h.edited_at && (
                              <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500 text-amber-400" title={`Edited by ${h.edited_by || "?"}`}>Edited</span>
                            )}
                          </div>
                        </div>
                        <div className="mono text-xs mt-1">{h.driver_name || "—"} · {h.submitted_at ? new Date(h.submitted_at).toLocaleString() : ""}</div>
                        {h.failed_items?.length > 0 && (
                          <div className="mono text-[11px] text-red-300 mt-0.5">
                            Failed: {h.failed_items.map((k) => CHECK_LABELS[k] || k).join(" · ")}
                          </div>
                        )}
                        {h.fault_note && <div className="text-xs text-amber-300 mt-0.5">Note: {h.fault_note}</div>}
                        {h.note && <div className="text-xs text-muted-industrial mt-0.5">{h.note}</div>}
                        {h.resolution_note && (
                          <div className="text-xs text-emerald-300 mt-0.5" data-testid={`resolution-${h.id}`}>
                            Fix: {h.resolution_note} ({h.resolution_date || (h.resolved_at || "").slice(0, 10)}) · {h.resolved_by || ""}
                          </div>
                        )}
                        {(h.hours_reading != null || h.km_reading != null) && (
                          <div className="mono text-[10px] text-muted-industrial mt-0.5">
                            {h.hours_reading != null ? `Hours: ${h.hours_reading}` : ""} {h.km_reading != null ? `· KM: ${h.km_reading}` : ""}
                          </div>
                        )}
                        {/* Actions */}
                        {h.kind === "pre_start" && (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {h.overall === "fail" && !h.resolved && (
                              <Button
                                size="sm"
                                onClick={() => setResolveLog(h)}
                                data-testid={`resolve-btn-${h.id}`}
                                className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white uppercase text-[10px] tracking-widest h-7"
                              >
                                <Check className="w-3 h-3 mr-1" /> Mark Resolved
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditLog(h)}
                              data-testid={`edit-btn-${h.id}`}
                              className="rounded-none border-industrial bg-transparent text-ink uppercase text-[10px] tracking-widest h-7"
                            >
                              <Pencil className="w-3 h-3 mr-1" /> Edit
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Resolve dialog */}
      <ResolveDialog log={resolveLog} onClose={() => setResolveLog(null)} onSaved={() => { setResolveLog(null); reloadHistory(); }} />

      {/* Edit pre-start dialog */}
      <EditPreStartDialog log={editLog} onClose={() => setEditLog(null)} onSaved={() => { setEditLog(null); reloadHistory(); }} />

      {/* Service edit dialog */}
      <ServiceDialog
        item={serviceTruck}
        onClose={() => setServiceTruck(null)}
        onSaved={() => { setServiceTruck(null); load(); }}
      />
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, tone, testid }) {
  const tones = {
    red: "border-red-600 text-red-300 bg-red-950/30",
    amber: "border-amber-500 text-amber-300 bg-amber-500/10",
    slate: "border-slate-500 text-slate-300 bg-slate-500/10",
    emerald: "border-emerald-500 text-emerald-300 bg-emerald-500/10",
  };
  return (
    <div className={`p-3 border-2 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <div className="mono text-[10px] uppercase tracking-widest">{label}</div>
      </div>
      <div className="mono text-3xl font-black mt-1">{value}</div>
    </div>
  );
}

function ServiceDialog({ item, onClose, onSaved }) {
  const [interval_, setInterval_] = useState(500);
  const [lastHours, setLastHours] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (item) {
      setInterval_(item.truck.service_hours_interval || 500);
      setLastHours(item.truck.last_service_hours || 0);
    }
  }, [item]);

  if (!item) return null;
  const t = item.truck;

  const markServiced = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/trucks/${t.id}/service`, { mark_serviced_now: true, service_hours_interval: Number(interval_) });
      toast.success(`Truck ${t.number} marked serviced`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/trucks/${t.id}/service`, {
        service_hours_interval: Number(interval_),
        last_service_hours: Number(lastHours),
      });
      toast.success("Service settings saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-md">
        <DialogHeader>
          <DialogTitle className="heading uppercase tracking-wider">Service · Truck {t.number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs mono">
            <div className="border border-industrial p-2">
              <div className="label-tag text-[9px]">Current Hours</div>
              <div className="text-lg font-black">{Math.round(t.current_hours || 0)}</div>
            </div>
            <div className="border border-industrial p-2">
              <div className="label-tag text-[9px]">Last Serviced @</div>
              <div className="text-lg font-black">{Math.round(t.last_service_hours || 0)}</div>
            </div>
          </div>
          <div>
            <Label className="label-tag">Service Interval (hours)</Label>
            <Input
              type="number"
              value={interval_}
              onChange={(e) => setInterval_(e.target.value)}
              className="rounded-none bg-transparent border-industrial mono"
              data-testid="service-interval-input"
            />
          </div>
          <div>
            <Label className="label-tag">Last Service Hours (manual)</Label>
            <Input
              type="number"
              value={lastHours}
              onChange={(e) => setLastHours(e.target.value)}
              className="rounded-none bg-transparent border-industrial mono"
              data-testid="last-service-hours-input"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={saveSettings} disabled={saving} className="rounded-none flex-1 border-industrial bg-transparent" data-testid="save-service-settings">
              Save
            </Button>
            <Button onClick={markServiced} disabled={saving} className="rounded-none flex-1 bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase tracking-widest text-xs" data-testid="mark-serviced-now">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Serviced Now
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Resolve a pre-start fault · v3.7 ───────────────────────────────
function ResolveDialog({ log, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [fixDate, setFixDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNote(""); setFixDate(new Date().toISOString().slice(0, 10)); }, [log?.id]);
  if (!log) return null;
  const submit = async () => {
    if (!note.trim()) return toast.error("Enter what was fixed");
    setSaving(true);
    try {
      await api.post(`/admin/maintenance-logs/${log.id}/resolve`, { note: note.trim(), fix_date: fixDate });
      toast.success("Fault marked resolved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!log} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-md">
        <DialogHeader>
          <DialogTitle className="heading uppercase tracking-wider">Resolve Fault</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-red-800/50 bg-red-950/20 p-2">
            <div className="mono text-[10px] uppercase tracking-widest text-red-400 font-bold">Reported {log.date}</div>
            {log.failed_items?.length > 0 && (
              <div className="mono text-[11px] text-red-300 mt-0.5">
                Failed: {(log.failed_items || []).map((k) => CHECK_LABELS[k] || k).join(" · ")}
              </div>
            )}
            {log.fault_note && <div className="text-xs text-amber-300 mt-1">{log.fault_note}</div>}
          </div>
          <div>
            <Label className="label-tag">What was fixed *</Label>
            <textarea
              data-testid="resolve-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. Replaced tail-light bulb"
              className="w-full bg-transparent border border-industrial mono text-sm p-2 rounded-none focus:border-primary-orange focus:outline-none"
            />
          </div>
          <div>
            <Label className="label-tag">Fix date</Label>
            <Input
              type="date"
              data-testid="resolve-date-input"
              value={fixDate}
              onChange={(e) => setFixDate(e.target.value)}
              className="rounded-none bg-transparent border-industrial mono"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-none flex-1 border-industrial bg-transparent" data-testid="resolve-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving || !note.trim()}
              data-testid="resolve-save"
              className="rounded-none flex-1 bg-emerald-600 hover:bg-emerald-500 text-white uppercase tracking-widest text-xs"
            >
              <Check className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : "Mark Resolved"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit a pre-start entry · v3.7 (fix driver mistakes) ────────────
const EDITABLE_CHECKS = [
  { k: "oil", label: "Oil" }, { k: "coolant", label: "Coolant" },
  { k: "tires", label: "Tyres" }, { k: "lights", label: "Lights" },
  { k: "brakes", label: "Brakes" }, { k: "mixer", label: "Mixer" },
  { k: "hydraulics", label: "Hydraulics" }, { k: "cleanliness", label: "Cleanliness" },
];
function EditPreStartDialog({ log, onClose, onSaved }) {
  const [checks, setChecks] = useState({});
  const [reason, setReason] = useState("");
  const [faultNote, setFaultNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (log) {
      setChecks({ ...(log.checks || {}) });
      setFaultNote(log.fault_note || "");
      setReason("");
    }
  }, [log?.id]);
  if (!log) return null;
  const submit = async () => {
    if (!reason.trim()) return toast.error("Provide an edit reason");
    setSaving(true);
    try {
      await api.patch(`/admin/maintenance-logs/${log.id}`, {
        edit_reason: reason.trim(), fault_note: faultNote, ...checks,
      });
      toast.success("Pre-start updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!log} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-lg">
        <DialogHeader>
          <DialogTitle className="heading uppercase tracking-wider">Edit Pre-Start · {log.date}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">
            Toggle any check the driver got wrong. All edits are audited.
          </div>
          <div className="grid grid-cols-2 gap-2">
            {EDITABLE_CHECKS.map((c) => {
              const ok = !!checks[c.k];
              return (
                <button
                  key={c.k}
                  type="button"
                  data-testid={`edit-check-${c.k}`}
                  onClick={() => setChecks({ ...checks, [c.k]: !ok })}
                  className={`p-2 border-2 text-left transition mono text-[11px] uppercase tracking-widest ${
                    ok ? "border-emerald-500/70 bg-emerald-500/10 text-emerald-300"
                       : "border-red-600 bg-red-950/40 text-red-300"
                  }`}
                >
                  {ok ? "✓ " : "✗ "}{c.label}
                </button>
              );
            })}
          </div>
          <div>
            <Label className="label-tag">Fault note</Label>
            <textarea
              value={faultNote}
              onChange={(e) => setFaultNote(e.target.value)}
              rows={2}
              data-testid="edit-fault-note"
              placeholder="Optional — clear or update the note"
              className="w-full bg-transparent border border-industrial mono text-sm p-2 rounded-none focus:border-primary-orange focus:outline-none"
            />
          </div>
          <div>
            <Label className="label-tag">Edit reason *</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="edit-reason"
              placeholder="e.g. Driver mis-tapped lights, actually working"
              className="rounded-none bg-transparent border-industrial mono"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-none flex-1 border-industrial bg-transparent" data-testid="edit-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving || !reason.trim()}
              data-testid="edit-save"
              className="rounded-none flex-1 bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase tracking-widest text-xs"
            >
              {saving ? "Saving…" : "Save Edit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

